import React, { useState, useEffect, useRef } from 'react';
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  Grid,
} from '@mui/material';
import { SingleChoiceQuestion } from './AssessmentQuestionType';
import AssessmentStepControButtonGroup from './AssessmentStepControlButtonGroup';
import {
  AssessmentQuestionAction,
  AssessmentQuestionActionType,
} from './AssessmentQuestionAction';
import APICaller, { MethodType } from '../common/APICaller';
import { useParams } from 'react-router-dom';
import FileDownload from '../common/FileDownload';

interface SingleChoiceQuestionStepProps {
  data: SingleChoiceQuestion;
  index: number;
  isLast: boolean;
  isFirst: boolean;
  goNext: () => void;
  goBack: () => void;
  handleSubmit: () => void;
  dispatch: React.Dispatch<AssessmentQuestionAction>;
}

const SingleChoiceQuestionStep: React.FC<SingleChoiceQuestionStepProps> = ({
  data,
  index,
  isLast,
  isFirst,
  goNext,
  goBack,
  handleSubmit,
  dispatch,
}) => {
  // Find initially selected option (only one should be chosen)
  const initialSelectedOption = Object.keys(data.answer).find(
    choice_id => data.answer[+choice_id].chosen
  );
  
  const [selectedOption, setSelectedOption] = useState<string>(
    initialSelectedOption || '',
  );
  const { assessmentId } = useParams();

  // Track last saved selection to prevent duplicate saves
  const lastSavedRef = useRef<string>(initialSelectedOption || '');
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const options = data.answer
    ? Object.keys(data.answer)
        .sort((a, b) => +a - +b)
        .map(choice_id => {
          return {
            id: choice_id,
            text: data.answer[+choice_id].text,
          };
        })
    : [];

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedOption(event.target.value);
  };

  // Autosave function
  const saveAnswer = (selection: string) => {
    // Skip if no selection or unchanged
    if (!selection || selection === lastSavedRef.current) {
      return Promise.resolve();
    }

    const url = data.answerId
      ? `/application/assessments/answers/${data.answerId}/mc/update`
      : `/application/assessments/questions/${data.id}/answer/mc/create`;
    const method = data.answerId ? MethodType.PUT : MethodType.POST;
    const body = {
      answer: {
        possibleChoiceIds: [+selection], // Single choice, but API expects array
      },
    };

    return APICaller(url, method, body)
      .then(res => {
        if (!res.errorMessage) {
          if (res.answerId) {
            dispatch({
              type: AssessmentQuestionActionType.UpdateMultipleChoice,
              payload: {
                question_index: index,
                answer: [+selection],
                answerId: res.answerId,
              },
            });
          } else {
            dispatch({
              type: AssessmentQuestionActionType.UpdateMultipleChoice,
              payload: {
                question_index: index,
                answer: [+selection],
              },
            });
          }
          lastSavedRef.current = selection;
          // Silent autosave - no notification
        }
      })
      .catch(e => {
        console.log(e);
      });
  };

  // Debounced autosave on selection change
  useEffect(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    if (selectedOption !== lastSavedRef.current && selectedOption) {
      saveTimeoutRef.current = setTimeout(() => {
        saveAnswer(selectedOption);
      }, 800); // 0.8 second delay (same as multiple choice)
    }

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [selectedOption]);

  const handleNext = async () => {
    // Cancel pending debounced save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Save immediately before navigating if there are unsaved changes
    if (selectedOption !== lastSavedRef.current && selectedOption) {
      await saveAnswer(selectedOption);
    }
    goNext();
  };

  const handleBack = async () => {
    // Cancel pending debounced save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Save immediately before navigating if there are unsaved changes
    if (selectedOption !== lastSavedRef.current && selectedOption) {
      await saveAnswer(selectedOption);
    }
    goBack();
  };

  const handleSubmitWithSave = async () => {
    // Cancel pending debounced save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Save immediately before submitting if there are unsaved changes
    if (selectedOption !== lastSavedRef.current && selectedOption) {
      await saveAnswer(selectedOption);
    }
    handleSubmit();
  };

  return (
    <>
      <FormControl component="fieldset">
        <Grid container direction="column" width="100%">
          <Typography variant="h6">{data.description}</Typography>
          {data.hasQuestionFile && (
            <FileDownload
              url={`/assessments/${assessmentId}/question/${data.id}/file/download`}
              displayedText="Download Question"
              width="45%"
            />
          )}
          <RadioGroup value={selectedOption} onChange={handleOptionChange}>
            {options.map(option => (
              <FormControlLabel
                key={option.id}
                value={option.id}
                control={<Radio />}
                label={option.text}
              />
            ))}
          </RadioGroup>
        </Grid>
      </FormControl>
      <AssessmentStepControButtonGroup
        isLast={isLast}
        isFirst={isFirst}
        handleNext={handleNext}
        handleBack={handleBack}
        handleSubmit={handleSubmitWithSave}
        showSaveBtn={false}
      />
    </>
  );
};

export default SingleChoiceQuestionStep;
